/* ------------------------------------------------------------------
   src/stores/user.js
   ------------------------------------------------------------------ */
import { writable, get } from 'svelte/store';
import { persist, createLocalStorage } from '@macfja/svelte-persistent-store';
import {
  /* read / write */
  doc,
  setDoc,
  getDoc,
  /* query helpers (for de-dupe) */
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { firestoreAuth } from '$lib/firebase';
import { db as getDb } from '$lib/firestore';          // Promise-based helper

/* ────────────────────────────────────────────────────────── */
/* 1️⃣  Local (persistent) user state                         */
/* ────────────────────────────────────────────────────────── */
export const userStore = persist(
  writable({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    acceptTerms: false,
    address: '',
    postCode: '',
    country: ''
  }),
  createLocalStorage(),
  'userStore'
);

export const userAuth = writable(null);

/* ────────────────────────────────────────────────────────── */
/* 2️⃣  Sync Firebase Auth → userStore                       */
/* ────────────────────────────────────────────────────────── */
if (typeof window !== 'undefined') {
  onAuthStateChanged(firestoreAuth, async (user) => {
    const defaults = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      acceptTerms: false,
      address: '',
      postCode: '',
      country: ''
    };

    const db = await getDb();                // real Firestore (or null)

    if (user) {
      let updated = { ...get(userStore) };

      if (user.email && db) {
        const ref = doc(db, 'users', user.email.toLowerCase());
        const snap = await getDoc(ref);

        updated = snap.exists()
          ? { ...defaults, ...snap.data() }
          : {
            ...defaults,
            firstName: user.displayName?.split(' ')[0] ?? '',
            lastName: user.displayName?.split(' ').slice(1).join(' ') ?? '',
            email: user.email.toLowerCase()
          };
      }

      if (user.isAnonymous) {
        updated = { ...defaults, ...updated }; // keep local anon edits
      }

      userStore.set(updated);
      userAuth.set(user);
    } else {
      userAuth.set(null);
      userStore.set(defaults);
    }
  });
}

/* ────────────────────────────────────────────────────────── */
/* 3️⃣  saveUserDetails – create/merge *single* Firestore doc */
/* ────────────────────────────────────────────────────────── */
export const saveUserDetails = async (partial) => {
  const db = await getDb();
  if (!db) {
    console.error('Firestore not available – cannot save user details.');
    return;
  }

  const authUser = get(userAuth);
  const email =
    authUser?.email?.toLowerCase() ||
    partial?.email?.toLowerCase();

  if (!email) {
    console.error('saveUserDetails called without an e-mail address.');
    return;
  }

  const data = { ...partial, email };   // ensure doc always contains email

  try {
    /* 1️⃣  Does any users doc already own this e-mail? */
    const match = await getDocs(
      query(collection(db, 'users'), where('email', '==', email))
    );

    const targetRef = match.empty
      ? doc(db, 'users', email)    // canonical path (email as ID)
      : match.docs[0].ref;         // existing UID-named or email-named doc

    await setDoc(targetRef, data, { merge: true });
    console.info(
      `User document for ${email} saved/updated at ${targetRef.path}.`
    );
  } catch (err) {
    console.error('Error saving user details:', err);
    throw err;
  }
};
